import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { SERVICES, ACCENTS } from "@/data/services";
import { MaskedLine, FadeIn, Reveal, Chapter } from "@/components/Reveal";

const WIDE = new Set([0, 4, 7]);

export default function Services() {
    useSEO(
        "Services | Digital Circle — Calgary Web, App & Marketing Studio",
        "Websites, web & mobile apps, custom software, branding, digital marketing, social media, tech setup and strategy consulting in Calgary."
    );

    return (
        <main className="pt-32 md:pt-44" data-testid="services-page">
            <section className="mx-auto max-w-7xl px-5 md:px-8">
                <FadeIn>
                    <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        <span className="ring-logo inline-block h-3.5 w-3.5" />
                        Services
                    </p>
                </FadeIn>
                <h1 className="mt-6 max-w-4xl text-5xl font-black tracking-tighter text-slate-900 md:text-7xl" data-testid="services-headline">
                    <MaskedLine delay={0.1}>Eleven ways we</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="gradient-text">move you forward.</span>
                    </MaskedLine>
                </h1>
                <FadeIn delay={0.4} className="mt-6 max-w-xl">
                    <p className="text-base leading-relaxed text-slate-600 md:text-lg">
                        One team for everything digital — from your first logo to the software that runs your whole operation.
                    </p>
                </FadeIn>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s, i) => {
                        const a = ACCENTS[s.accent];
                        return (
                            <Reveal
                                key={s.slug}
                                delay={(i % 3) * 0.07}
                                className={WIDE.has(i) ? "lg:col-span-2" : ""}
                            >
                                <div
                                    data-testid={`service-card-${s.slug}`}
                                    className={`group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 md:p-10 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.18)] ${a.hoverBorder}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <span className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${a.soft} transition-transform duration-300 group-hover:scale-110`}>
                                            <s.icon className={`h-7 w-7 ${a.icon}`} />
                                        </span>
                                        <span className={`mt-2 h-1 w-10 rounded-full ${a.bar} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                                    </div>
                                    <h2 className="mt-7 text-xl font-bold tracking-tight text-slate-900 md:text-2xl">{s.title}</h2>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600 md:text-base">{s.description}</p>
                                    <Link
                                        to={`/contact?service=${encodeURIComponent(s.title)}`}
                                        data-testid={`service-request-${s.slug}`}
                                        className="mt-7 inline-flex w-fit items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-900 transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                                    >
                                        Request this service
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal className="mt-20">
                    <div className="relative overflow-hidden rounded-[2rem] bg-slate-950 px-8 py-14 text-center md:py-20">
                        <div className="conic-ring spin-slow absolute -left-24 -top-24 h-72 w-72 opacity-30 blur-2xl" aria-hidden="true" />
                        <h2 className="relative text-3xl font-black tracking-tighter text-white md:text-4xl">Not sure which one you need?</h2>
                        <p className="relative mx-auto mt-4 max-w-lg text-sm leading-relaxed text-slate-400 md:text-base">
                            Tell us the problem — we'll tell you honestly what it takes to solve it.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="services-bottom-cta"
                            className="group relative mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-900 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
                        >
                            Get a Free Quote
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
